import { Button, IconButton, Paper, SxProps, Theme, Tooltip, useMediaQuery } from "@mui/material";
import { ReactNode, useCallback, useState } from "react";
import { useI18n } from "../../hooks/useI18n";
import { theme } from "../../layout/Theme";

import StyledDialog from "../mui/StyledDialog";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LightStyledDatepicker from "../mui/LightStyledDatepicker";
import dayjs from "dayjs";

interface DeleteButtonIconProps {
  deleteRecord: () => Promise<void>;
  refreshCallback: () => void;
  title: string;
  modalTitle: string;
  modalContent: string | ReactNode;
  icon: ReactNode;
  testId?: string;
  label?: string;
  isDisabled?: boolean;
  sx?: SxProps<Theme> | undefined;
  variant?: "text" | "outlined" | "contained";
  type?: "button" | "icon";
  buttonWidth?: string;
  birthDateInput?: Date | null;
  setBirthDateInput?: (value: Date | null) => void;
  confirmDisabled?: boolean;
  dateFormat: string;
}

export function DeleteButtonIcon(props: DeleteButtonIconProps) {
  const { t } = useI18n();
  const {
    deleteRecord,
    refreshCallback,
    title,
    modalTitle,
    modalContent,
    icon,
    testId,
    label,
    isDisabled = false,
    sx,
    variant = "contained",
    type = "button",
    buttonWidth = "auto",
    confirmDisabled = false,
    birthDateInput = null,
    setBirthDateInput,
    dateFormat,
  } = props;

  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [datePickerOpened, setDatePickerOpened] = useState<boolean>(false);

  const breakpointMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const handleClickOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleClose = () => {
    setDeleteDialogOpen(false);
  };

  const deleteHandler = useCallback(async () => {
    setErrorMessage(undefined);
    setIsSending(true);
    try {
      await deleteRecord();
      refreshCallback();
      setDeleteDialogOpen(false);
    } catch (_e) {
      setErrorMessage(t("common.alerts.alert_notification"));
    } finally {
      setIsSending(false);
    }
  }, [deleteRecord, refreshCallback, t]);

  const pickerValue = birthDateInput ? dayjs(birthDateInput) : null;

  return (
    <>
      {type === "button" ? (
        <Button
          variant={variant}
          onClick={handleClickOpen}
          data-testid={testId}
          disabled={isDisabled}
          sx={{
            ...sx,
            width: { xs: "auto", md: buttonWidth },
          }}
          size={breakpointMdUp ? "medium" : "small"}
          startIcon={icon}
          disableElevation
        >
          {title}
        </Button>
      ) : (
        <Tooltip title={title} arrow>
          <IconButton onClick={handleClickOpen} data-testid={testId} disabled={isDisabled}>
            {icon}
          </IconButton>
        </Tooltip>
      )}
      <StyledDialog
        open={deleteDialogOpen}
        onClose={handleClose}
        data-testid={`${testId}Dialog`}
        title={modalTitle}
        dialogActions={
          <Button
            onClick={deleteHandler}
            color="error"
            autoFocus
            variant="outlined"
            data-testid={`${testId}DialogDeleteCta`}
            startIcon={<DeleteOutlineIcon />}
            loading={isSending}
            disabled={confirmDisabled || isSending}
          >
            {label ? label : t("common.cta.delete")}
          </Button>
        }
        errorMessage={errorMessage}
      >
        <Paper elevation={0} sx={{ display: "flex", flexDirection: "column", p: 2 }}>
          {modalContent}
          <LightStyledDatepicker
            placeholder={dateFormat}
            onChange={(value) => {
              if (!setBirthDateInput) return;
              if (value === null) {
                setBirthDateInput(null);
                return;
              }
              setBirthDateInput(value.toDate());
            }}
            maxDate={dayjs.utc()}
            open={datePickerOpened}
            value={pickerValue}
            onOpen={() => setDatePickerOpened(true)}
            onClose={() => setDatePickerOpened(false)}
            required
          />
        </Paper>
      </StyledDialog>
    </>
  );
}
